import { defineStore } from 'pinia'
import axios from 'axios'
import { buildTenantApiPath } from '@/utils/http'
import { sanitizeTenantSlug } from '../constants/tenant'
import { useTenantAppStore } from './tenantApps'

interface UserBinding {
  provider: string
  label: string
  description?: string
  bound: boolean
  external_id?: string | null
  display_name?: string | null
  bound_at?: string | null
  meta?: Record<string, any> | null
}

interface Profile {
  id: number
  name: string
  email?: string
  notification_email?: string | null
  role?: string
  tenant_id?: number | null
  tenant_slug?: string | null
  tenant_name?: string | null
  tenant_short_name?: string | null
  tenant_logo_url?: string | null
  tenant_login_title?: string | null
  phone?: string | null
  avatar_url?: string | null
  attributes?: Record<string, any>
  profile_schema?: ProfileSchema
  bindings?: UserBinding[]
  tenant_features?: string[]
  tenant_permissions?: string[]
  role_groups?: string[]
  capabilities?: string[]
}

interface ProfileSchema {
  role: string
  groups: Array<{
    key: string
    label: string
    fields: Array<{
      key: string
      label: string
      type: string
      location?: string
      required?: boolean
    }>
  }>
}

interface TokenResponse {
  access_token: string
  token_type?: string
  expires_at?: string
  abilities?: string[]
  user?: Profile
}

export interface IdentitySwitchAccount {
  key: string
  channel: 'tenant' | 'admin'
  role: string
  role_label: string
  default_route: string
  visible_app_keys: string[]
  tenant: {
    slug: string
    name: string
    status: string
  } | null
  user: {
    id: number
    name: string
  }
}

export interface IdentitySwitchOptionsResponse {
  accounts: IdentitySwitchAccount[]
  switch_challenge_token: string
  expires_in: number
}

export interface IdentitySwitchCurrentResponse {
  identity: IdentitySwitchAccount
  can_request_switch: boolean
  phone_masked: string | null
}

export interface IdentitySwitchSmsResponse {
  sent: boolean
  phone_masked: string
  cooldown: number
  expires_in: number
}

const TOKEN_KEY = 'smart_access_token'
const TENANT_KEY = 'smart:tenantSlug'
const LEGACY_TENANT_KEY = 'smart_tenant_slug'
const CHANNEL_KEY = 'smart_login_channel'

const localToken = localStorage.getItem(TOKEN_KEY)
const sessionToken = localToken ? null : sessionStorage.getItem(TOKEN_KEY)
const storedToken = localToken ?? sessionToken ?? ''
const storedRemember = Boolean(localToken)
const rawStoredTenant = storedRemember
  ? localStorage.getItem(TENANT_KEY) ?? localStorage.getItem(LEGACY_TENANT_KEY) ?? ''
  : sessionStorage.getItem(TENANT_KEY) ?? sessionStorage.getItem(LEGACY_TENANT_KEY) ?? ''
const storedTenant = rawStoredTenant ? sanitizeTenantSlug(rawStoredTenant) : ''
const storedChannel = (storedRemember
  ? (localStorage.getItem(CHANNEL_KEY) as 'tenant' | 'admin' | 'auto' | null)
  : (sessionStorage.getItem(CHANNEL_KEY) as 'tenant' | 'admin' | 'auto' | null)) ?? 'auto'

function persistValue(key: string, value: string | undefined, remember: boolean) {
  const primary = remember ? localStorage : sessionStorage
  const secondary = remember ? sessionStorage : localStorage
  if (value && value.length) {
    primary.setItem(key, value)
  } else {
    primary.removeItem(key)
  }
  secondary.removeItem(key)
}

function clearLegacyTenantSlug() {
  localStorage.removeItem(LEGACY_TENANT_KEY)
  sessionStorage.removeItem(LEGACY_TENANT_KEY)
}

if (storedToken) {
  axios.defaults.headers.common.Authorization = `Bearer ${storedToken}`
}

if (storedTenant) {
  axios.defaults.headers.common['X-Smart-Tenant'] = storedTenant
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: storedToken,
    tenantSlug: storedTenant,
    channel: storedChannel,
    remember: storedRemember,
    profile: null as Profile | null,
    abilities: [] as string[],
    loading: false
  }),
  actions: {
    setChannel(mode?: 'tenant' | 'admin' | 'auto') {
      const next = mode ?? 'auto'
      this.channel = next
      persistValue(CHANNEL_KEY, next, this.remember)
    },
    setTenantSlug(slug?: string) {
      const normalized = slug ? sanitizeTenantSlug(slug) : ''
      if (slug && !normalized) {
        this.tenantSlug = ''
        persistValue(TENANT_KEY, undefined, this.remember)
        clearLegacyTenantSlug()
        delete axios.defaults.headers.common['X-Smart-Tenant']
        return
      }
      if (normalized) {
        if (normalized !== this.tenantSlug) {
          this.tenantSlug = normalized
        }
        persistValue(TENANT_KEY, normalized, this.remember)
        clearLegacyTenantSlug()
        axios.defaults.headers.common['X-Smart-Tenant'] = normalized
      } else {
        this.tenantSlug = ''
        persistValue(TENANT_KEY, undefined, this.remember)
        clearLegacyTenantSlug()
        delete axios.defaults.headers.common['X-Smart-Tenant']
      }
    },
    setRememberMode(remember: boolean) {
      if (this.remember === remember) {
        return
      }
      this.remember = remember
      this.persistToken(this.token || undefined)
      persistValue(CHANNEL_KEY, this.channel, this.remember)
      persistValue(TENANT_KEY, this.tenantSlug || undefined, this.remember)
    },
    persistToken(token?: string) {
      if (token) {
        persistValue(TOKEN_KEY, token, this.remember)
      } else {
        persistValue(TOKEN_KEY, undefined, this.remember)
      }
    },
    async ensureSession(slug?: string) {
      if (!this.token) {
        throw new Error('缺少访问令牌')
      }

      axios.defaults.headers.common.Authorization = `Bearer ${this.token}`

      if (this.channel === 'admin' || this.profile?.role === 'super_admin') {
        this.setTenantSlug(undefined)
        const { data } = await axios.get('/api/v1/admin/me')
        const profile = this.resolveUser(data)
        if (!profile) {
          throw new Error('用户信息缺失')
        }
        this.profile = profile
        this.abilities = Array.isArray(data?.abilities) ? data.abilities : this.abilities
        return
      }

      const fallbackTenant = this.remember
        ? localStorage.getItem(TENANT_KEY) ?? localStorage.getItem(LEGACY_TENANT_KEY)
        : sessionStorage.getItem(TENANT_KEY) ?? sessionStorage.getItem(LEGACY_TENANT_KEY)
      const effectiveSlug = sanitizeTenantSlug(slug ?? this.tenantSlug ?? fallbackTenant ?? '')

      if (!effectiveSlug) {
        throw new Error('缺少学校标识')
      }

      if (this.profile && this.profile.tenant_slug === effectiveSlug) {
        return
      }

      this.setTenantSlug(effectiveSlug)

      const { data } = await axios.get(buildTenantApiPath(effectiveSlug, 'me'))
      const profile = this.resolveUser(data)
      if (!profile) {
        throw new Error('用户信息缺失')
      }
      this.profile = profile
      this.abilities = Array.isArray(data?.abilities) ? data.abilities : this.abilities
    },
    async login(payload: {
      channel?: 'tenant' | 'admin' | 'auto'
      tenant?: string
      method?: 'password' | 'sms'
      email?: string
      password?: string
      phone?: string
      code?: string
      remember?: boolean
      accountKey?: string
    }) {
      this.loading = true
      try {
        const channel = payload.channel ?? 'auto'
        const method = payload.method ?? 'password'
        const remember = payload.remember ?? true
        const body: Record<string, string> = {
          channel,
          method
        }
        if (channel === 'tenant') {
          const normalizedTenant = sanitizeTenantSlug(payload.tenant ?? this.tenantSlug ?? '')
          if (!normalizedTenant) {
            throw new Error('缺少学校标识')
          }
          body.tenant = normalizedTenant
        }

        if (method === 'sms') {
          if (!payload.phone || !payload.code) {
            throw new Error('缺少手机号或验证码')
          }
          body.phone = payload.phone
          body.code = payload.code
          if (payload.accountKey) {
            body.account_key = payload.accountKey
          }
        } else {
          if (!payload.email || !payload.password) {
            throw new Error('缺少邮箱或密码')
          }
          body.email = payload.email
          body.password = payload.password
        }

        const { data } = await axios.post<TokenResponse>('/api/v1/auth/token', body)
        this.token = data.access_token
        this.abilities = data.abilities ?? []
        axios.defaults.headers.common.Authorization = `Bearer ${this.token}`

        const resolvedChannel: 'tenant' | 'admin' = data.user?.tenant_id ? 'tenant' : 'admin'
        this.setRememberMode(remember)
        this.persistToken(this.token)
        this.setChannel(resolvedChannel)

        const tenantSlug = data.user?.tenant_slug ?? (resolvedChannel === 'tenant' ? payload.tenant : '')
        this.setTenantSlug(resolvedChannel === 'tenant' ? tenantSlug || undefined : undefined)
        this.profile = this.resolveUser(data)

        if (resolvedChannel === 'tenant') {
          await this.ensureSession(this.tenantSlug)
        } else {
          await this.ensureSession()
        }
  } catch (error) {
    this.logout()
    throw error
  } finally {
    this.loading = false
  }
},
    async bootstrap(slug?: string) {
      if (!this.token) return
      try {
        await this.ensureSession(slug)
      } catch (error) {
        console.warn('读取用户信息失败，将清除登录状态', error)
        this.logout()
      }
    },
    async requestIdentitySwitchCode(): Promise<IdentitySwitchSmsResponse> {
      const { data } = await axios.post<IdentitySwitchSmsResponse>('/api/v1/auth/identity-switch/sms-code')
      return data
    },
    async identitySwitchOptions(code: string): Promise<IdentitySwitchOptionsResponse> {
      const { data } = await axios.post<IdentitySwitchOptionsResponse>('/api/v1/auth/identity-switch/options', {
        code
      })
      return data
    },
    async fetchIdentityContext(): Promise<IdentitySwitchCurrentResponse> {
      const { data } = await axios.get<IdentitySwitchCurrentResponse>('/api/v1/auth/identity-switch/current')
      return data
    },
    async switchIdentity(payload: {
      accountKey: string
      switchChallengeToken: string
      password?: string
    }): Promise<TokenResponse> {
      this.loading = true
      let tokenReplaced = false
      try {
        const { data } = await axios.post<TokenResponse>('/api/v1/auth/identity-switch', {
          account_key: payload.accountKey,
          switch_challenge_token: payload.switchChallengeToken,
          password: payload.password
        })

        const resolvedChannel: 'tenant' | 'admin' = data.user?.tenant_id ? 'tenant' : 'admin'
        const tenantSlug = resolvedChannel === 'tenant' ? data.user?.tenant_slug ?? '' : ''
        const appStore = useTenantAppStore()

        this.profile = null
        this.abilities = []
        appStore.reset()

        this.token = data.access_token
        tokenReplaced = true
        this.abilities = data.abilities ?? []
        axios.defaults.headers.common.Authorization = `Bearer ${this.token}`
        this.persistToken(this.token)
        this.setChannel(resolvedChannel)
        this.setTenantSlug(resolvedChannel === 'tenant' ? tenantSlug || undefined : undefined)

        if (resolvedChannel === 'tenant') {
          await this.ensureSession(this.tenantSlug)
          await appStore.ensureApps(this.tenantSlug, true)
        } else {
          await this.ensureSession()
        }

        return data
      } catch (error) {
        if (tokenReplaced) {
          this.logout()
        }
        throw error
      } finally {
        this.loading = false
      }
    },
    setProfile(profile: Profile | null) {
      this.profile = profile
    },
    hasTenantPermission(permissionKeys: string | string[]): boolean {
      const keys = (Array.isArray(permissionKeys) ? permissionKeys : [permissionKeys])
        .map((key) => String(key ?? '').trim())
        .filter((key) => key.length > 0)

      if (!keys.length) {
        return true
      }
      if (this.profile?.role === 'super_admin') {
        return true
      }

      const permissions = Array.isArray(this.profile?.tenant_permissions)
        ? this.profile.tenant_permissions
        : []
      const abilities = Array.isArray(this.abilities) ? this.abilities : []

      return keys.every((key) =>
        permissions.includes(key) || abilities.includes(`tenant.permission.${key}`)
      )
    },
    hasRoleGroup(roleGroupKeys: string | string[]): boolean {
      const keys = (Array.isArray(roleGroupKeys) ? roleGroupKeys : [roleGroupKeys])
        .map((key) => String(key ?? '').trim())
        .filter((key) => key.length > 0)

      if (!keys.length || this.profile?.role === 'super_admin') {
        return true
      }

      const roleGroups = Array.isArray(this.profile?.role_groups) ? this.profile.role_groups : []
      return keys.some((key) => roleGroups.includes(key))
    },
    hasCapability(capabilityKeys: string | string[]): boolean {
      const keys = (Array.isArray(capabilityKeys) ? capabilityKeys : [capabilityKeys])
        .map((key) => String(key ?? '').trim())
        .filter((key) => key.length > 0)

      if (!keys.length || this.profile?.role === 'super_admin') {
        return true
      }

      const capabilities = Array.isArray(this.profile?.capabilities) ? this.profile.capabilities : []
      const abilities = Array.isArray(this.abilities) ? this.abilities : []
      return keys.some((key) =>
        capabilities.includes(key) ||
        abilities.includes(key) ||
        abilities.includes(`capability.${key}`) ||
        abilities.includes(`tenant.feature.${key}`)
      )
    },
    canUseTenantOperation(permissionKeys: string | string[]): boolean {
      if (this.profile?.role === 'super_admin') {
        return true
      }
      if (this.profile?.role !== 'tenant_admin') {
        return true
      }
      return this.hasTenantPermission(permissionKeys)
    },
    resolveUser(payload: unknown): Profile | null {
      if (!payload || typeof payload !== 'object') {
        return null
      }
      const candidate = (payload as Record<string, unknown>).user ?? payload
      if (!candidate || typeof candidate !== 'object') {
        return null
      }
      return candidate as Profile
    },
    logout() {
      this.token = ''
      this.profile = null
      this.abilities = []
      this.persistToken(undefined)
      this.setChannel('auto')
      this.setTenantSlug(undefined)
      this.remember = false
      delete axios.defaults.headers.common.Authorization
      try {
        const appStore = useTenantAppStore()
        appStore.reset()
      } catch (error) {
        console.warn('[auth] 清理应用状态失败', error)
      }
    }
  }
})
